import {Flex} from 'src/design/atoms/Flex';
import {Group} from 'src/design/atoms/Group';
import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import type {
  SourceEntityRequest,
  TaskDefinitionRequest,
} from 'src/api/ProactApiClient';
import {
  EntityIdentifierEnum,
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {
  useLazyGetTaskDefinitionDetailsQuery,
  useUpdateTaskDefinitionMutation,
} from 'src/redux/queries/taskDefinition';
import {SaveButton} from 'src/design/molecules/SaveButton';
import {FormTransitionBar} from 'src/app/forms/FormTransitionBar';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {LockGuard} from 'src/app/lock-mgt/LockGuard';
import {TaskFeatureEditor} from 'src/app/tasks/TaskFeatureEditor';
import {buildEditTaskRequest} from 'src/app/tasks/taskRequestTransport';
import {
  GetModuleEnumFromTaskType,
  GetUrlSectionFromTaskType,
  TaskPageTitleDict,
  TaskPillConfig,
} from 'src/app/tasks/taskHelpers';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useStateMachine} from 'src/shared/hooks/api/useStateMachine';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {ApiEndpoints} from 'src/shared/utils/ApiCaller';
import {useTaskBreadcrumbs} from 'src/shared/hooks/useTaskBreadcrumbs';
import {capitalize} from 'src/shared/utils/formatters';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IStateMachine} from 'src/types/StateMachine';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface EditTaskPageProps {
  taskType: TaskTypeEnum;
  featureFlag: FeatureFlagEnum;
}

const EditTaskPage: React.FC<EditTaskPageProps> = ({taskType, featureFlag}) => {
  useSetDocumentTitle(capitalize(GetUrlSectionFromTaskType(taskType)));
  const navigate = useAppNavigate();
  const module = GetModuleEnumFromTaskType(taskType);

  const {setAlert} = useAlert();
  const {taskId} = useParams();

  const [editItem, setEditItem] = useState<any>();
  const [isLoadingTask, setIsLoadingTask] = useState<boolean>(true);
  const [isSavePending, setIsSavePending] = useState<boolean>(false);

  const isLoading = isLoadingTask;

  const [triggerGetDetails] = useLazyGetTaskDefinitionDetailsQuery();
  const [updateTaskDefinition] = useUpdateTaskDefinitionMutation();

  useEffect(() => {
    const getData = async () => {
      if (taskId === undefined || isNaN(+taskId)) {
        return;
      }

      try {
        const response = await triggerGetDetails(+taskId).unwrap();

        const item = {
          ...response,
          previewDueAt: response.dueInDays ? response.dueAt : undefined,
          inlineAttachments: response.metadata?.inlineAttachments,
          contentPdfUrl: response.contentPdfUrl ?? undefined,
          dueInDays: response.dueInDays
            ? String(response.dueInDays)
            : undefined,
          ...{
            sourceEntities: {
              selectAllEntitiesType: response.metadata?.selectAllEntityType,
              entities: response.sourceEntities.map(entity => ({
                entityId: entity.entityId,
                entityType: entity.entityType,
              })),
            } as SourceEntityRequest,
          },
        };

        setEditItem(item);
      } catch (error) {
        setAlert(parseErrorMessage(error), 'error');
      }
      setIsLoadingTask(false);
    };

    void getData();
  }, [taskId]);

  const onSubmitted = () =>
    navigate(PathUtility.TaskView(taskType, taskId as string, 'content'), {
      replace: true,
    });

  const breadcrumbs = useTaskBreadcrumbs({
    taskType,
    mode: 'edit',
    taskId,
    itemSubject: editItem?.subject,
  });

  const pills = editItem
    ? TaskPillConfig(
        editItem.state,
        editItem.taskType,
        editItem.participation,
        editItem.approvalState
      )
    : [];

  const canSave = editItem?.locked !== true;

  const [machineBase] = useStateMachine({
    url: ApiEndpoints.getTaskDefinitionStateMachine,
  });
  const machine = machineBase
    ? ({
        states: machineBase.states,
        transitions: machineBase.transitions.map(t => ({
          ...t,
          title: `Save & ${t.title}`,
        })),
      } as IStateMachine)
    : undefined;

  return (
    <TaskFeatureEditor
      taskType={taskType}
      taskState={editItem?.state}
      initialData={editItem ? toFormDataV2(editItem) : undefined}
      resetKey={editItem?.id}>
      {({formApi, form, getFeatureFields}) => {
        const submit = async (request?: TaskDefinitionRequest) => {
          if (!(await formApi.validateForm())) {
            return {continue: false};
          }
          setIsSavePending(true);
          const body = buildEditTaskRequest({
            item: editItem,
            featureFields: getFeatureFields(),
            transitionFields: request,
          });
          try {
            await updateTaskDefinition(body).unwrap();
            setAlert(
              `${TaskPageTitleDict[taskType].singular} was successfully updated`,
              'success'
            );
            return {continue: true};
          } catch (error) {
            setAlert(parseErrorMessage(error), 'error');
            setIsSavePending(false);
            return {continue: false};
          }
        };
        const handleSave = async () => {
          const result = await submit();
          if (result.continue) onSubmitted();
        };
        const handleTransitioning = () =>
          submit({skipOnUpdatedSideEffect: true});

        return (
          <ContentWrapper featureFlag={featureFlag}>
            {!isLoading ? (
              <Flex direction="column" w="100%">
                <BackBar
                  fallbackTo={PathUtility.TaskView(
                    taskType,
                    taskId ?? '',
                    'content'
                  )}
                  breadcrumbs={breadcrumbs}
                />
                <PageHeader>
                  <PageHeader.Title>
                    {editItem ? editItem.subject : 'Item not found'}
                  </PageHeader.Title>
                  {editItem?.internalIdentifier && (
                    <PageHeader.Meta>
                      {editItem.internalIdentifier}
                    </PageHeader.Meta>
                  )}
                  {pills.length > 0 && (
                    <PageHeader.Badges>
                      {pills.map((pill, index) => (
                        <PageHeader.Badge
                          key={index}
                          color={pill.color}
                          variant="light">
                          {pill.text}
                        </PageHeader.Badge>
                      ))}
                    </PageHeader.Badges>
                  )}
                  <PageHeader.Badges>
                    <PageHeader.Badge color="yellow" variant="light">
                      Editing
                    </PageHeader.Badge>
                  </PageHeader.Badges>
                  {canSave && (
                    <PageHeader.Actions>
                      <PageHeader.Action
                        variant="save"
                        loading={isSavePending}
                        onClick={handleSave}>
                        Save
                      </PageHeader.Action>
                    </PageHeader.Actions>
                  )}
                </PageHeader>
                {editItem && (
                  <LockGuard
                    id={editItem.id}
                    type={EntityIdentifierEnum.TaskDefinition}
                    lockCache={editItem.locked}>
                    {form}

                    <Group align="start">
                      {machine ? (
                        <FormTransitionBar
                          preTransitionButtonNodes={[
                            <SaveButton
                              loading={isSavePending}
                              onClick={handleSave}
                            />,
                          ]}
                          onTransitioning={handleTransitioning}
                          module={module}
                          stateMachine={machine}
                          stateId={editItem.state}
                          transitionBaseUrl={`task-definition/${editItem.id}/transition`}
                          onRefreshTransition={onSubmitted}
                          invalidateTagsOnComplete={[
                            'TaskDefinitions',
                            'TaskAssignments',
                          ]}
                        />
                      ) : (
                        <SaveButton
                          loading={isSavePending}
                          onClick={handleSave}
                        />
                      )}
                    </Group>
                  </LockGuard>
                )}
              </Flex>
            ) : (
              <TableBodyRender
                isLoading={isLoading}
                hasData={editItem !== undefined}
              />
            )}
          </ContentWrapper>
        );
      }}
    </TaskFeatureEditor>
  );
};

// Register the routes with the new system
[
  {
    type: TaskTypeEnum.Approval,
    featureFlag: FeatureFlagEnum.Approvals,
    module: ModuleEnum.Approvals,
  },
  {
    type: TaskTypeEnum.ToolboxTalk,
    featureFlag: FeatureFlagEnum.ToolboxTalks,
    module: ModuleEnum.ToolboxTalks,
  },
  {
    type: TaskTypeEnum.Form,
    featureFlag: FeatureFlagEnum.Forms,
    module: ModuleEnum.AssignedForms,
  },
].forEach(item => {
  routeRegistry.registerPageRoute({
    backNavigation: 'form',
    path: PathUtility.TaskEdit(item.type, ':taskId'),
    element: (
      <EditTaskPage taskType={item.type} featureFlag={item.featureFlag} />
    ),
    module: item.module,
    operation: PermissionOperationEnum.Update,
    isPrivate: true,
  });
});

export default EditTaskPage;
